import isUndefined from "lodash/isUndefined";
import mapValues from "lodash/mapValues";
import isEmpty from "lodash/isEmpty";
import Length from "../../../source/js/shared/models/Geometry/Length";
import { detectBrowser } from "@/functions/browser";

/**
 * Counts the amount of decimal points.
 *
 * @param {number} x The number to check.
 *
 * @return {number} The decimal count.
 */
export function countDecimals( x: number ): number {
	if ( x && x % 1 ) {
		return x.toString().length - x.toString().indexOf( "." ) - 1 || 0;
	}
	return 0;
}

/**
 * Converts a string to a number, before make sure the comma is a period
 * then strip all non-numeric characters and parse to a float
 *
 * @param {string|number} value The string to strip.
 *
 * @return {number} The stripped string.
 */
export function stripNonNumeric( value: string|number ): number {
	return parseFloat(value.toString().replace(",", ".").replace(/[^0-9.]/g, ""));
}

/**
 * Checks whether the passed value ends with a period or comma.
 *
 * @param {string|number} value The string to check.
 *
 * @return {boolean} Whether it ends with a period or a comma.
 */
export function endsWithSeparator( value: string|number ): boolean {
	if ( typeof value === 'number' ) {
		value = value.toString();
	}

	return value.endsWith('.') || value.endsWith(',');
}

/**
 * Formats the passed value and limits it to the amount of decimals.
 *
 * @param {string|number} value The
 * @param {number} decimals The amount of decimals to return. Defaults to 2.
 */
export function limitDecimals( value: string|number, decimals = 2 ): number {
	return parseFloat( parseFloat( toDecimalPoint( value ) ).toFixed( decimals ) );
}

/**
 * Formats a number according to locale, with support for partial inputs such as `10,` or `10.`
 *
 * @param {string|number} value The value to format.
 *
 * @return {string} The formatted value.
 */
export function formatToLocaleString( value: string|number ): string {
	if ( isUndefined( value ) ) {
		return;
	}

	value = value.toString();

	const locale = navigator.language || 'nl-NL';
	const parsedValue = parseFloat( value.replace( ',', '.' ) );
	const separator = ( 0.1 ).toLocaleString( locale ).substring( 1, 2 );

	if ( isNaN( parsedValue ) ) {
		return value.replace( /[.,]/, separator );
	}

	const formatted = parsedValue.toLocaleString( locale, { maximumFractionDigits: 1, useGrouping: false } );

	return endsWithSeparator( value ) ? `${formatted}${separator}` : formatted;
}

/**
 * Formats a number for use in an input field.
 *
 * @param {string|number} value The value to format.
 * @return {string|number} The formatted value.
 */
export function formatForInputField( value: string|number ): string|number {
	if ( detectBrowser() !== 'Safari' ) {
		return value;
	}

	if ( isUndefined( value ) ) {
		return '';
	}

	// Special case: if x is 0, return an empty string for easier inputting for the user
	if ( value === 0 ) {
		return '';
	}

	value = stripNonNumeric( value.toString() );

	const formatted = formatToLocaleString( value );

	if ( formatted === 'NaN' ) {
		return '';
	}

	return formatted;
}

/**
 * Converts the units from CM to MM (or vice versa).
 *
 * @param {object} dimensions The dimensions to convert.
 * @param {boolean} fromCM Whether to convert from CMs. Defaults to true.
 *
 * @return {object} The converted units.
 */
export function convertUnits( dimensions: object, fromCM = true ): object {
	return mapValues( dimensions, ( ( dimension: number ) => {
		return fromCM ? Length.from_cm( dimension ).in_mm() : new Length( dimension ).in_cm();
	} ) );
}

/**
 * Converts the passed value to a number.
 *
 * @param {any} value The value to convert.
 *
 * @returns {number} A numeric value of the passed value.
 */
export function toNumber( value: any ): number {
	if ( typeof value == 'number' ) {
		return value;
	}

	if ( isEmpty( value ) ) {
		return 0;
	}

	return parseInt( value, 10 );
}

/**
 * Converts the passed value to a store-compliant decimal string.
 *
 * @param {string|number} value The value to convert.
 *
 * @returns {string} The compliant value.
 */
export function toDecimalPoint( value: string|number ): string {
	value = stripNonNumeric( value );

	if ( typeof value !== 'string' ) {
		value = value.toString();
	}

	return value.replace( ',', '.' );
}
